export const CONVERSATION_NEAR_BOTTOM_PX = 96;

export type MessageScrollIdentity = {
  messageId: string;
  organizationMemberId: number | null;
  origin?: string | null;
  direction?: "INBOUND" | "OUTBOUND";
};

export type MessageListChange =
  | { kind: "initial" }
  | { kind: "unchanged" }
  | { kind: "status_only" }
  | { kind: "replaced" }
  | { kind: "prepend"; prepended: MessageScrollIdentity[] }
  | { kind: "append"; appended: MessageScrollIdentity[] }
  | {
      kind: "mixed";
      prepended: MessageScrollIdentity[];
      appended: MessageScrollIdentity[];
    };

export type ThreadScrollReaction = {
  followBottom: boolean;
  preservePrepend: boolean;
  inboundIncrement: number;
  resetInboundCount: boolean;
  clearLocalSend: boolean;
};

export function isNearBottom(
  el: { scrollTop: number; scrollHeight: number; clientHeight: number },
  thresholdPx: number = CONVERSATION_NEAR_BOTTOM_PX,
): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;
}

export function formatNewInboundCount(count: number): string | null {
  if (count <= 0) return null;
  if (count > 99) return "99+";
  return `+${count}`;
}

export function applyThreadScrollToLatest(
  el: {
    scrollTop: number;
    scrollHeight: number;
    scrollTo: (options: ScrollToOptions) => void;
  },
  behavior: ScrollBehavior,
): void {
  if (behavior === "auto") {
    el.scrollTop = el.scrollHeight;
    return;
  }
  el.scrollTo({ top: el.scrollHeight, behavior });
}

/**
 * Shared scroll-ref ownership across AnimatePresence enter/exit.
 *
 * Attach (`next != null`) always wins.
 * Detach (`next == null`) must not wipe a newer active pane: when `detaching` is
 * provided, clear only if it is still the registered node; when omitted (React
 * object-ref / bare null cleanup), keep `current`.
 */
export function applyActiveScrollRefUpdate<T>(
  current: T | null,
  next: T | null,
  detaching?: T | null,
): T | null {
  if (next != null) return next;
  if (detaching != null) {
    return Object.is(current, detaching) ? null : current;
  }
  return current;
}

export function countInboundMessages(messages: readonly MessageScrollIdentity[]): number {
  let count = 0;
  for (const message of messages) {
    if (message.direction === "INBOUND" || (!message.direction && message.organizationMemberId == null && message.origin == null)) {
      count += 1;
    }
  }
  return count;
}

export function inboundCountAfterViewportChange(count: number, nearBottom: boolean): number {
  return nearBottom ? 0 : count;
}

export type ScrollIntent = "user" | "localSend" | "autoFollow";

export function scrollBehaviorFor(input: {
  intent: ScrollIntent;
  reducedMotion: boolean;
}): ScrollBehavior {
  if (input.reducedMotion) return "auto";
  switch (input.intent) {
    case "user":
    case "localSend":
      return "smooth";
    case "autoFollow":
      return "auto";
    default: {
      const _exhaustive: never = input.intent;
      return _exhaustive;
    }
  }
}

/** Timeline system pills are not customer inbound and must not bump the new-message counter. */
export function systemEventScrollIdentity(eventId: string): MessageScrollIdentity {
  return {
    messageId: `event:${eventId}`,
    organizationMemberId: null,
    origin: "SYSTEM",
    direction: "OUTBOUND",
  };
}

export function toMessageScrollIdentities(
  messages: readonly MessageScrollIdentity[],
): MessageScrollIdentity[] {
  return messages.map((message) => ({
    messageId: message.messageId,
    organizationMemberId: message.organizationMemberId,
    origin: message.origin,
    direction: message.direction,
  }));
}

export function classifyMessageListChange(
  previous: readonly MessageScrollIdentity[],
  next: readonly MessageScrollIdentity[],
): MessageListChange {
  if (previous.length === 0) {
    return next.length === 0 ? { kind: "unchanged" } : { kind: "initial" };
  }
  if (next.length === 0) {
    return { kind: "replaced" };
  }

  const previousIds = previous.map((message) => message.messageId);
  const nextIds = next.map((message) => message.messageId);
  if (
    previousIds.length === nextIds.length &&
    previousIds.every((id, index) => id === nextIds[index])
  ) {
    return { kind: "status_only" };
  }

  const nextIdSet = new Set(nextIds);
  for (const id of previousIds) {
    if (!nextIdSet.has(id)) {
      return { kind: "replaced" };
    }
  }

  const firstPreviousIndex = nextIds.indexOf(previousIds[0]!);
  if (firstPreviousIndex < 0) {
    return { kind: "replaced" };
  }

  const previousSlice = nextIds.slice(firstPreviousIndex, firstPreviousIndex + previousIds.length);
  const previousContiguous =
    previousSlice.length === previousIds.length &&
    previousSlice.every((id, index) => id === previousIds[index]);

  if (previousContiguous) {
    const prepended = next.slice(0, firstPreviousIndex);
    const appended = next.slice(firstPreviousIndex + previousIds.length);
    if (prepended.length > 0 && appended.length > 0) {
      return { kind: "mixed", prepended, appended };
    }
    if (prepended.length > 0) {
      return { kind: "prepend", prepended };
    }
    if (appended.length > 0) {
      return { kind: "append", appended };
    }
    return { kind: "status_only" };
  }

  const previousIdSet = new Set(previousIds);
  const prepended: MessageScrollIdentity[] = [];
  const appended: MessageScrollIdentity[] = [];
  for (const message of next) {
    if (previousIdSet.has(message.messageId)) continue;
    const index = nextIds.indexOf(message.messageId);
    if (index < firstPreviousIndex) prepended.push(message);
    else appended.push(message);
  }
  if (prepended.length > 0 && appended.length > 0) {
    return { kind: "mixed", prepended, appended };
  }
  if (prepended.length > 0) {
    return { kind: "prepend", prepended };
  }
  if (appended.length > 0) {
    return { kind: "append", appended };
  }
  return { kind: "replaced" };
}

export function decideThreadScrollReaction(input: {
  change: MessageListChange;
  isNearBottom: boolean;
  pendingLocalSend: boolean;
}): ThreadScrollReaction {
  const idle: ThreadScrollReaction = {
    followBottom: false,
    preservePrepend: false,
    inboundIncrement: 0,
    resetInboundCount: false,
    clearLocalSend: false,
  };

  switch (input.change.kind) {
    case "unchanged":
    case "status_only":
      return idle;
    case "initial":
    case "replaced":
      return {
        followBottom: true,
        preservePrepend: false,
        inboundIncrement: 0,
        resetInboundCount: true,
        clearLocalSend: input.pendingLocalSend,
      };
    case "prepend":
      return {
        ...idle,
        preservePrepend: true,
      };
    case "append":
      return reactionForAppend(input.change.appended, input, false);
    case "mixed":
      return reactionForAppend(input.change.appended, input, true);
    default: {
      const _exhaustive: never = input.change;
      return _exhaustive;
    }
  }
}

function reactionForAppend(
  appended: readonly MessageScrollIdentity[],
  input: { isNearBottom: boolean; pendingLocalSend: boolean },
  preservePrepend: boolean,
): ThreadScrollReaction {
  if (input.pendingLocalSend) {
    return {
      followBottom: true,
      preservePrepend,
      inboundIncrement: 0,
      resetInboundCount: true,
      clearLocalSend: true,
    };
  }
  if (input.isNearBottom) {
    return {
      followBottom: true,
      preservePrepend,
      inboundIncrement: 0,
      resetInboundCount: true,
      clearLocalSend: false,
    };
  }
  return {
    followBottom: false,
    preservePrepend,
    inboundIncrement: countInboundMessages(appended),
    resetInboundCount: false,
    clearLocalSend: false,
  };
}
